import { UWLogo } from 'assets/images/logo';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { cn } from 'utils';
import { LogoutIcon } from 'assets/icons/logout';
import AccountInfo from '../account-info';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import CustomButton from 'components/ui/custom-button';
import { useAuth } from 'hooks/useAuth';
import SidebarMainItem from 'components/ui/sidebar-main-item';
import SidebarSubItem from 'components/ui/sidebar-sub-item';

interface SubItem {
  id: number;
  title: string;
  href: string;
  subItems?: SubItem[];
  onClick?: () => void;
}

interface SidebarItem {
  id: number;
  title: string;
  icon: () => JSX.Element;
  href: string;
  subItems?: SubItem[];
  onClick?: () => void;
}

interface SidebarProps {
  items: SidebarItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ items }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const { user, logout } = useAuth();

  const toggleDropdown = useCallback((id: number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  useEffect(() => {
    const findAndExpandParents = () => {
      const newExpandedItems = new Set<number>();

      const checkItemAndSubItems = (
        items: SidebarItem[] | SubItem[],
        parentPath: string = ''
      ) => {
        items.forEach((item) => {
          const fullPath = `${parentPath}${item.href}`;

          if (pathname.startsWith(fullPath) && item.subItems) {
            newExpandedItems.add(item.id);

            if (item.subItems) {
              checkItemAndSubItems(item.subItems, fullPath);
            }
          }
        });
      };

      checkItemAndSubItems(items);
      setExpandedItems(newExpandedItems);
    };

    findAndExpandParents();
  }, [pathname, items]);

  const isPathActive = useCallback(
    (basePath: string, itemPath: string) => {
      const fullPath = `${basePath}${itemPath}`;
      if (fullPath === '') {
        return pathname === '/' || pathname === '';
      }
      return pathname === fullPath || pathname.startsWith(`${fullPath}/`);
    },
    [pathname]
  );

  const renderSubItems = useCallback(
    (subItems: SubItem[], parentPath: string, level: number = 1) => {
      return (
        <div className={cn('space-y-1', level === 1 ? 'pl-8' : 'pl-4')}>
          {subItems.map((subItem) => {
            const isActive = isPathActive(parentPath, subItem.href);
            const hasSubItems = subItem.subItems && subItem.subItems.length > 0;
            const isExpanded = expandedItems.has(subItem.id);
            const fullPath = `${parentPath}${subItem.href}`;

            return (
              <div key={subItem.id}>
                <SidebarSubItem
                  title={subItem.title}
                  href={fullPath}
                  isExpanded={isExpanded}
                  isActive={isActive}
                  hasSubItems={hasSubItems}
                  onToggle={() => hasSubItems && toggleDropdown(subItem.id)}
                  onClick={subItem.onClick}
                />
                {subItem.subItems &&
                  subItem.subItems.length > 0 &&
                  isExpanded &&
                  renderSubItems(
                    subItem.subItems,
                    `${parentPath}${subItem.href}`,
                    level + 1
                  )}
              </div>
            );
          })}
        </div>
      );
    },
    [expandedItems, isPathActive, toggleDropdown]
  );

  return (
    <div className="fixed w-w_sidebar max-h-h_sidebar h-[100vh] flex flex-col rounded-r-2xl bg-[url('./assets/images/sidebar-background-gradient.png')]">
      <div className="p-4">
        <div className="flex items-center gap-2">
          <UWLogo />
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-4">
          <p className="text-[12px] font-small text-muted-foreground text-[#5E6B7C]">
            Main
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2 scrollbar-hide">
          {items.map((item) => {
            const isExpanded = expandedItems.has(item.id);
            const isActive = isPathActive('', item.href);
            const hasSubItems = item.subItems && item.subItems.length > 0;

            return (
              <div key={item.id}>
                <SidebarMainItem
                  title={item.title}
                  href={item.href}
                  icon={item.icon}
                  isExpanded={isExpanded}
                  isActive={isActive}
                  hasSubItems={hasSubItems}
                  onToggle={() => hasSubItems && toggleDropdown(item.id)}
                  onClick={item.onClick}
                />
                {item.subItems &&
                  item.subItems.length > 0 &&
                  isExpanded &&
                  renderSubItems(item.subItems, item.href)}
              </div>
            );
          })}
        </div>

        <div className="px-4 pt-2 pb-4 mt-auto">
          <AccountInfo name={user?.name || ''} role="Senior Underwriter" />
          <CustomButton
            variant={EButtonVariant.PRIMARY}
            label="Logout"
            prefixIcon={<LogoutIcon />}
            onClick={logout}
            className="mt-[16px] w-[100%]"
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
